import { useEffect, useRef, useState } from "react";
import { CircleAlert } from "lucide-react";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import { ghostBtnClass } from "@/components/common/surfaceClasses";
import { matchablePct } from "@/components/audience-data/audienceDataFormat";
import type { SsdQualitySummary } from "@/lib/ssdSalesClient";

function Count({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-gray-500 dark:text-zinc-400">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

export function AudienceDataQualityMenu(props: {
  quality: SsdQualitySummary | null;
  issueCount: number;
  skippedLastJob: number;
}) {
  const [open, setOpen] = useState(false);
  const [ackedCount, setAckedCount] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const q = props.quality;
  const hot = props.issueCount > 0 && ackedCount !== props.issueCount;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className={
          "inline-flex h-8 w-8 items-center justify-center rounded-full border text-sm " +
          (hot
            ? "border-amber-500 bg-amber-100 text-amber-950 hover:bg-amber-200 dark:border-amber-400 dark:bg-amber-900/70 dark:text-amber-50 dark:hover:bg-amber-800"
            : ghostBtnClass)
        }
        aria-label={
          hot
            ? `Data quality, ${props.issueCount} issue${props.issueCount === 1 ? "" : "s"}`
            : "Data quality"
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
        {...{ [FIREFLY_TARGET_ATTR]: "audience-data-quality" }}
      >
        <CircleAlert className="h-4 w-4" aria-hidden />
      </button>
      {open ? (
        <div className="absolute right-0 z-20 mt-1 w-80 max-w-[calc(100vw-2rem)] space-y-3 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
          <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Data quality</p>
          {q ? (
            <dl className="grid grid-cols-2 gap-2 text-sm text-gray-700 dark:text-zinc-300">
              <Count label="Sales" value={q.total} />
              <Count label={`Matchable (${matchablePct(q.matchable, q.total)})`} value={q.matchable} />
              <Count label="Not matchable" value={q.non_matchable} />
              <Count label="Missing email" value={q.missing_email} />
              <Count label="Invalid email" value={q.invalid_email} />
              <Count label="Missing phone" value={q.missing_phone} />
              <Count label="Invalid phone" value={q.invalid_phone} />
              <Count label="Missing ZIP" value={q.missing_zip} />
              <Count label="Duplicate email" value={q.duplicate_email} />
              <Count label="Duplicate phone" value={q.duplicate_phone} />
            </dl>
          ) : (
            <p className="text-sm text-gray-500 dark:text-zinc-400">No quality summary yet.</p>
          )}
          {props.skippedLastJob > 0 ? (
            <p className="text-xs text-gray-600 dark:text-zinc-300">
              Last import skipped {props.skippedLastJob} row{props.skippedLastJob === 1 ? "" : "s"}.
            </p>
          ) : null}
          <p className="text-xs text-gray-600 dark:text-zinc-400">
            Open Unmatchable on this card to page through issue rows.
          </p>
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => {
              setAckedCount(props.issueCount);
              setOpen(false);
            }}
          >
            Acknowledge
          </button>
        </div>
      ) : null}
    </div>
  );
}
